import React, { useEffect, useState } from "react";
import Button from "../../../components/Buttons/Button";
import DataTable from "../../../components/DataTable/DataTable";
import { IColInfo } from "../../../components/DataTable/Interfaces";
import SelectListField from "../../../components/Fields/SelectListField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import SpinnerOverlay from "../../../components/Spinner/SpinnerOverlay";
import { useNotifications } from "../../../contexts/notificationContext";
import ItemRevisionModel from "../../../models/ItemRevisionModel";
import ProjectItemRevisionModel from "../../../models/ProjectItemRevisionModel";
import WorkflowDefinitionModel from "../../../models/WorkflowDefinitionModel";
import WorkflowModel from "../../../models/WorkflowModel";
import DataService from "../../../services/data.service";
import { ProjectPopupProps } from "../../../types/PopupProps";
import { DataResult, Result } from "../../../types/Result";
import FormatUtils from "../../../utils/FormatUtils";

interface StartWorkflowModel {
    taskId: number
    projectId: number
    itemRevisionIds: number[]
    startWorkflowDefinitionId: number
    workflowExecutionTypeId?: number
}

const StartItemWorkflowsPopup: React.FC<ProjectPopupProps> = ({ show, onHide, projectId, projectTypeId }) => {
    const [formData, setFormData] = useState<StartWorkflowModel>();
    const [tableData, setTableData] = useState<ItemRevisionModel[]>([])
    const [itemStatusTypes, setItemStatusTypes] = useState<string[]>()
    const [showSubItems, setShowSubItems] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false)
    const { handleResult, handleException } = useNotifications();
    const formatUtils = new FormatUtils()

    const workflowDefinitions = useSelectListOptions('api/v1/selectlist/ProjectItemWorkflowDefinitions?projectTypeId=' + projectTypeId, '-- Select workflow');
    const workflowExecutionTypes = useSelectListOptions(`api/v1/selectlist/WorkflowExecutionTypes`, '-- Select execution type');

    useEffect(() => {
        const defaultFormData = {
            taskId: 1,
            projectId: projectId,
            itemRevisionIds: [],
            startWorkflowDefinitionId: 0,
            workflowExecutionTypeId: 1,
        }
        setFormData(defaultFormData);
    }, [show, projectId]);

    useEffect(() => {
        if (formData && formData.startWorkflowDefinitionId > 0) {
            DataService
                .get<ItemRevisionModel[]>(`api/v1/ItemRevisions/WorkflowStartableItemsByProject?projectId=${projectId}&workflowDefinitionId=${formData.startWorkflowDefinitionId}&workflowExecutionTypeId=${formData.workflowExecutionTypeId}`)
                .then(response => {
                    let statusTypes: string[] = []
                    response.forEach(x => {
                        x.itemRevisionStatuses.forEach(y => {
                            var endDateStatusType = formatUtils.dateFromString(y.itemStatus.itemStatusType.endDate)
                            var endDateStatus = formatUtils.dateFromString(y.itemStatus.endDate)
                            if ((endDateStatusType === undefined || endDateStatusType > new Date(Date.now())) &&
                                (endDateStatus === undefined || endDateStatus > new Date(Date.now()))) {
                                if (y.isLatest && statusTypes.includes(y.itemStatus.itemStatusType.name) == false) {
                                    statusTypes.push(y.itemStatus.itemStatusType.name);
                                }
                            }
                        })
                    });
                    setItemStatusTypes(statusTypes)

                    setTableData([...response])

                })
                .catch(e => handleException(e));
        }
    }, [projectId, formData?.startWorkflowDefinitionId, formData?.workflowExecutionTypeId])

    function getWorkflowDefinition(workflowDefinitionId: number) {
        setShowSpinner(true)
        DataService
            .get<WorkflowDefinitionModel>('api/v1/WorkflowDefinitions/' + workflowDefinitionId)
            .then(response => {
                setShowSpinner(false)
                if (formData) {
                    if (response.workflowExecutionTypeId)
                        formData.workflowExecutionTypeId = response.workflowExecutionTypeId
                    else
                        formData.workflowExecutionTypeId = 1

                    if (!response.workflowExecutionTypeId)
                        setShowSubItems(true)
                    else 
                        setShowSubItems(false)
                }
            })
            .catch(e => {
                handleException(e)
                setShowSpinner(false)
            });
    }


    function submit() {
        if (tableData) {
            var selectedItems = tableData.filter(x => x['_checkbox_Select'] == true);
            if (formData && selectedItems.length > 0) {
                formData.itemRevisionIds = selectedItems.map(x => x.id)
                setShowSpinner(true)
                DataService
                    .post<Result>(`api/v1/ProjectItemRevisions/TasksForMultipleItems`, formData)
                    //.post<DataResult<WorkflowModel>>(`api/v1/Workflow/StartWorkflows`, formData)
                    .then(response => {
                        setShowSpinner(false)
                        handleResult(response);
                        if (response.isSuccesful === true && onHide) {
                            // ToDo: navigate to started workflow
                            onHide();
                        }
                    })
                    .catch(e => { handleException(e); setShowSpinner(false); });
            }
        }
    }

    function onFieldChanged(name: string, value: string): void {
        if (name === 'startWorkflowDefinitionId') {
            if (formData) {
                formData.workflowExecutionTypeId = 0
                setFormData(formData)
            }
            if (value)
                getWorkflowDefinition(Number(value))
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'startWorkflowDefinitionId', label: 'Workflow', labelSize: 2, validationRules: [{ type: 'isRequired' }] },
            { name: 'workflowExecutionTypeId', label: 'Execution type', labelSize: 2 },
            { name: 'itemRevisionIds', label: 'Items', labelSize: 2 }
        ],
        handleSubmit: submit,
        showErrors: 'always',
        handleFieldChanged: onFieldChanged
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    function getStatusColor(itemRevision: ItemRevisionModel, colInfo: IColInfo): string | undefined {
        if (itemRevision.itemStatus)
            return itemRevision.itemStatus.color;
    }

    function getStatus(itemRevision: ItemRevisionModel, colInfo: IColInfo): string | undefined {
        const status = colInfo.dataProp;
        let result = ''

        itemRevision.itemRevisionStatuses.forEach(y => {
            if (y.isLatest == true && y.itemStatus.itemStatusType.name == status) {
                result = y.itemStatus.name;
            }
        })
        return result;
    }

    function onCheckboxButtonClick(selectedRows: ProjectItemRevisionModel[]) {
        if (tableData) {
            const select = tableData.length !== selectedRows.length
            tableData.forEach(x => x['_checkbox_Select'] = select);
            setTableData([...tableData])
        }
    }

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { checkboxButton: { text: "Select", icon: 'fa-check', color: 'green', onClick: onCheckboxButtonClick } },
        { getCellBackgroundColor: getStatusColor, sort: false, width: 5 },
        { title: 'Item Nr', dataProp: 'item', dataType: 'item', sort: 'asc' },
        { title: 'Revision', dataProp: 'revisionNumber', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Naam', dataProp: 'item.name' },
        { title: 'Design phase', dataProp: 'designPhase' },
    ];

    if (itemStatusTypes) {
        itemStatusTypes.forEach((value, index) => {
            colInfos.push({ title: value, dataProp: value, getCellData: getStatus });
        });
    }

    return <>
        <Popup show={show} onHide={onHide} size={'xl'} title={'Start project-item workflows'} >
            <Popup.Body>
                <SelectListField {...getFieldProps('startWorkflowDefinitionId')} options={workflowDefinitions} />
                {showSubItems && <SelectListField {...getFieldProps('workflowExecutionTypeId')} options={workflowExecutionTypes} />}

                <div style={{ marginTop: 24 }}>
                    <DataTable
                        name='dtProjectStartItemWorkflows'
                        colInfos={colInfos}
                        tableData={tableData}
                        />
                </div>
            </Popup.Body>
            <Popup.Footer>
                <Button {...getSubmitButtonProps()} text='Start workflows' fill />
            </Popup.Footer>
        </Popup>
        {showSpinner && <SpinnerOverlay />}
    </>
}
export default StartItemWorkflowsPopup;